import React, { useState, useEffect } from 'react';
import './App.css';
import BlockGroup from './BlockGroup';
import { rotateMatrix } from './utils';

interface StuckBlock {
  row: number;
  col: number;
  color: string;
}

interface MovingBlockGroup {
  id: string;
  pattern: number[][];
  blockType: string; // Add blockType
  startDelay: number;
  isActive: boolean;
  optimalCol: number;
  startingRotation: number;
  optimalRotation: number;
}

// Add BoardCell and Board types
interface BoardCell {
  occupied: boolean;
  color?: string;
}
type Board = BoardCell[][];

function App() {
  const [blockSize, setBlockSize] = useState(100);
  const [gridColumns, setGridColumns] = useState(0);
  // Remove stuckBlocks, add board
  const totalRows = 12;
  const [board, setBoard] = useState<Board>(
    Array.from({ length: totalRows }, () =>
      Array.from({ length: gridColumns || 10 }, () => ({ occupied: false }))
    )
  );
  const [movingBlockGroups, setMovingBlockGroups] = useState<MovingBlockGroup[]>([]);
  const [flickeringRows, setFlickeringRows] = useState<Set<number>>(new Set());
  const [flickerOpacity, setFlickerOpacity] = useState(1);
  const [pendingNewBlock, setPendingNewBlock] = useState<boolean>(true);

  // Declare block patterns as an object with keys for each block type
  const blockPatterns: { [key: string]: number[][][] } = {
    square: [
      // 0°
      [
        [0,0,0,0],
        [0,1,1,0],
        [0,1,1,0],
        [0,0,0,0]
      ],
      // 90°
      [
        [0,0,0,0],
        [0,1,1,0],
        [0,1,1,0],
        [0,0,0,0]
      ],
      // 180°
      [
        [0,0,0,0],
        [0,1,1,0],
        [0,1,1,0],
        [0,0,0,0]
      ],
      // 270°
      [
        [0,0,0,0],
        [0,1,1,0],
        [0,1,1,0],
        [0,0,0,0]
      ]
    ],
    T: [
      // 0°
      [
        [0,0,0,0],
        [0,1,0,0],
        [1,1,1,0],
        [0,0,0,0]
      ],
      // 90°
      [
        [0,0,0,0],
        [0,1,0,0],
        [0,1,1,0],
        [0,1,0,0]
      ],
      // 180°
      [
        [0,0,0,0],
        [0,0,0,0],
        [1,1,1,0],
        [0,1,0,0]
      ],
      // 270°
      [
        [0,0,0,0],
        [0,1,0,0],
        [1,1,0,0],
        [0,1,0,0]
      ]
    ],
    L: [
      // 0°
      [
        [0,0,0,0],
        [0,0,1,0],
        [1,1,1,0],
        [0,0,0,0]
      ],
      // 90°
      [
        [0,0,0,0],
        [0,1,0,0],
        [0,1,0,0],
        [0,1,1,0]
      ],
      // 180°
      [
        [0,0,0,0],
        [0,0,0,0],
        [1,1,1,0],
        [1,0,0,0]
      ],
      // 270°
      [
        [0,0,0,0],
        [1,1,0,0],
        [0,1,0,0],
        [0,1,0,0]
      ]
    ],
    line: [
      // 0°
      [
        [1,1,1,1],
        [0,0,0,0],
        [0,0,0,0],
        [0,0,0,0]
      ],
      // 90°
      [
        [0,0,1,0],
        [0,0,1,0],
        [0,0,1,0],
        [0,0,1,0]
      ],
      // 180°
      [
        [1,1,1,1],
        [0,0,0,0],
        [0,0,0,0],
        [0,0,0,0]
      ],
      // 270°
      [
        [0,0,1,0],
        [0,0,1,0],
        [0,0,1,0],
        [0,0,1,0]
      ]
    ],
    S: [
      // 0°
      [
        [0,0,0,0],
        [0,1,1,0],
        [1,1,0,0],
        [0,0,0,0]
      ],
      // 90°
      [
        [0,0,0,0],
        [0,1,0,0],
        [0,1,1,0],
        [0,0,1,0]
      ],
      // 180°
      [
        [0,0,0,0],
        [0,0,0,0],
        [0,1,1,0],
        [1,1,0,0]
      ],
      // 270°
      [
        [0,0,0,0],
        [1,0,0,0],
        [1,1,0,0],
        [0,1,0,0]
      ]
    ],
    Z: [
      // 0°
      [
        [0,0,0,0],
        [1,1,0,0],
        [0,1,1,0],
        [0,0,0,0]
      ],
      // 90°
      [
        [0,0,0,0],
        [0,0,1,0],
        [0,1,1,0],
        [0,1,0,0]
      ],
      // 180°
      [
        [0,0,0,0],
        [0,0,0,0],
        [1,1,0,0],
        [0,1,1,0]
      ],
      // 270°
      [
        [0,0,0,0],
        [0,1,0,0],
        [1,1,0,0],
        [1,0,0,0]
      ]
    ],
    J: [
      // 0°
      [
        [0,0,0,0],
        [1,0,0,0],
        [1,1,1,0],
        [0,0,0,0]
      ],
      // 90°
      [
        [0,0,0,0],
        [0,1,1,0],
        [0,1,0,0],
        [0,1,0,0]
      ],
      // 180°
      [
        [0,0,0,0],
        [0,0,0,0],
        [1,1,1,0],
        [0,0,1,0]
      ],
      // 270°
      [
        [0,0,0,0],
        [0,1,0,0],
        [0,1,0,0],
        [1,1,0,0]
      ]
    ]
  };

  // Function to get the nth block pattern
  // function getBlockPattern(n: number): number[][] {
  //   return blockPatterns[n % blockPatterns.length];
  // }

  useEffect(() => {
    const calculateBlockSize = () => {
      const screenHeight = window.innerHeight;
      const screenWidth = window.innerWidth;
      
      // Calculate block size to fit exactly 12 rows
      const calculatedBlockSize = Math.floor(screenHeight / 12);
      
      // Calculate how many columns fit
      const calculatedColumns = Math.floor(screenWidth / calculatedBlockSize);
      
      setBlockSize(calculatedBlockSize);
      setGridColumns(calculatedColumns);
    };

    calculateBlockSize();
    window.addEventListener('resize', calculateBlockSize);
    
    return () => window.removeEventListener('resize', calculateBlockSize);
  }, []);

  // Common helper to create a new block group for the current board state
  const createNewBlockGroupForBoard = (currentBoard: Board): MovingBlockGroup => {
    const totalColumns = Math.floor(window.innerWidth / blockSize);
    const totalRows = 12;
    const blockType = getRandomBlockType();
    const { startingCol: optimalCol, rotations: optimalRotation } = getBestHorizontalOffsetForBoard(
      blockType,
      currentBoard,
      totalColumns,
      totalRows
    );
    const startingRotation = Math.floor(Math.random()*4);
    const pattern = rotateMatrix(blockPatterns[blockType], startingRotation);

    return {
      id: `block-group-${Date.now()}`,
      pattern,
      blockType,
      startDelay: 0,
      isActive: true,
      optimalCol,
      startingRotation,
      optimalRotation,
    };
  };

  // Centralised effect to handle all new block-group creation whenever pendingNewBlock is true
  useEffect(() => {
    if (!pendingNewBlock) return;
    if (movingBlockGroups.length > 0) return;

    const newBlockGroup = createNewBlockGroupForBoard(board);
    setMovingBlockGroups([newBlockGroup]);
    setPendingNewBlock(false);
  }, [pendingNewBlock, movingBlockGroups.length, board, blockSize]);

  // Handle flicker animation for disappearing rows
  useEffect(() => {
    if (flickeringRows.size === 0) {
      return;
    }

    // Capture the rows to remove at the start of the effect
    const rowsToRemove = Array.from(flickeringRows);
    let flickCount = 0;
    const FLICK_DURATION = 100; // 100ms per flick
    const TOTAL_FLICKS = 2;

    const flickerInterval = setInterval(() => {
      flickCount++;
      setFlickerOpacity(prev => prev === 1 ? 0.3 : 1);

      if (flickCount >= TOTAL_FLICKS * 2) {
        // Animation complete, remove the rows
        clearInterval(flickerInterval);
        setBoard(prevBoard => {
          const updatedBoard = removeRowsFromBoard(prevBoard, rowsToRemove);
          return updatedBoard;
        });
        setFlickeringRows(new Set());
        setFlickerOpacity(1);
        setPendingNewBlock(true);
      }
    }, FLICK_DURATION);

    return () => clearInterval(flickerInterval);
  }, [flickeringRows]);

  // Create an array of indices for the grid
  const gridSize = 12 * gridColumns;
  const blocks = Array.from({ length: gridSize }, (_, index) => index);

  // Helper function to rotate a 2D array 90 degrees clockwise
  // const rotateMatrix = (matrix: number[][]): number[][] => {
  //   const rows = matrix.length;
  //   const cols = matrix[0].length;
  //   const rotated = Array.from({ length: cols }, () => Array(rows).fill(0));
    
  //   for (let i = 0; i < rows; i++) {
  //     for (let j = 0; j < cols; j++) {
  //       rotated[j][rows - 1 - i] = matrix[i][j];
  //     }
  //   }
  //   return rotated;
  // };

  // Function to get a random block pattern and rotation
  const getRandomBlockType = () => {
    const blockTypes = Object.keys(blockPatterns);
    return blockTypes[Math.floor(Math.random() * blockTypes.length)];
  };

  const getRandomPattern = () => {
    const blockTypes = Object.keys(blockPatterns);
    const blockType = blockTypes[Math.floor(Math.random() * blockTypes.length)];
    const rotation = Math.floor(Math.random() * 4);
    return {
      blockType,
      rotation,
      pattern: rotateMatrix(blockPatterns[blockType], rotation)
    };
  };

  // Helper to find leftmost and rightmost filled columns in the pattern
  const getPatternColBounds = (pattern: number[][]) => {
    let minCol = pattern.length > 0 ? pattern[0].length - 1 : 0;
    let maxCol = 0;
    let hasBlock = false;
    for (let row = 0; row < pattern.length; row++) {
      for (let col = 0; col < pattern[row].length; col++) {
        if (pattern[row][col] === 1) {
          hasBlock = true;
          if (col < minCol) minCol = col;
          if (col > maxCol) maxCol = col;
        }
      }
    }
    return hasBlock ? { minCol, maxCol } : { minCol: 0, maxCol: 0 };
  };

  // Helper to find the lowest row where a pattern can be placed
  const findLowestRowForPattern = (
    pattern: number[][],
    startingCol: number,
    board: Board,
    totalColumns: number,
    totalRows: number
  ): number => {
    let lastNonEmptyRow = 0;
    for (let i = pattern.length - 1; i >= 0; i--) {
      if (pattern[i].some(cell => cell === 1)) {
        lastNonEmptyRow = i;
        break;
      }
    }
    let row = -lastNonEmptyRow;
    let yLowest = row;
    let canMove = true;
    while (canMove) {
      let collision = false;
      for (let patternRow = 0; patternRow < pattern.length; patternRow++) {
        for (let patternCol = 0; patternCol < pattern[patternRow].length; patternCol++) {
          if (pattern[patternRow][patternCol] === 1) {
            const worldRow = row + patternRow;
            const worldCol = startingCol + patternCol;
            if (
              worldRow >= totalRows ||
              worldCol < 0 ||
              worldCol >= totalColumns ||
              board[worldRow]?.[worldCol]?.occupied
            ) {
              collision = true;
              break;
            }
          }
        }
        if (collision) break;
      }
      if (!collision) {
        yLowest = row;
        row++;
      } else {
        canMove = false;
      }
    }
    return yLowest;
  };

  // Helper to simulate placing a block at its lowest position and return the temporary board
  const simulateBlockPlacement = (
    pattern: number[][],
    startingCol: number,
    board: Board,
    totalColumns: number,
    totalRows: number
  ): Board => {
    // Find the lowest row where the pattern can be placed
    const yLowest = findLowestRowForPattern(pattern, startingCol, board, totalColumns, totalRows);

    // Create a temporary board with the block placed
    const tempBoard = board.map(row => row.map(cell => ({ ...cell })));
    for (let patternRow = 0; patternRow < pattern.length; patternRow++) {
      for (let patternCol = 0; patternCol < pattern[patternRow].length; patternCol++) {
        if (pattern[patternRow][patternCol] === 1) {
          const worldRow = yLowest + patternRow;
          const worldCol = startingCol + patternCol;
          if (worldRow >= 0 && worldRow < totalRows && worldCol >= 0 && worldCol < totalColumns) {
            // Ensure the row and column exist in the tempBoard
            if (!tempBoard[worldRow]) {
              tempBoard[worldRow] = Array.from({ length: totalColumns }, () => ({ occupied: false }));
            }
            if (!tempBoard[worldRow][worldCol]) {
              tempBoard[worldRow][worldCol] = { occupied: false };
            }
            tempBoard[worldRow][worldCol] = { occupied: true, color: '#4444ff' };
          }
        }
      }
    }

    return tempBoard;
  };



  // Function to calculate the lowest row reached and count unoccupied blocks on that row
  const getLowestRowByUnoccupiedBlocks = (
    pattern: number[][],
    startingCol: number,
    board: Board,
    totalColumns: number,
    totalRows: number
  ) => {
    // Find the lowest row where the pattern can be placed
    const yLowest = findLowestRowForPattern(pattern, startingCol, board, totalColumns, totalRows);

    // Count total unoccupied blocks on the entire board
    let unoccupiedCount = 0;
    for (let row = 0; row < totalRows; row++) {
      for (let col = 0; col < totalColumns; col++) {
        if (!board[row]?.[col]?.occupied) {
          unoccupiedCount++;
        }
      }
    }

    return {
      lowestRow: yLowest,
      unoccupiedBlocks: unoccupiedCount
    };
  };

  // Function to count unoccupied blocks that are fully surrounded by occupied blocks
  const getSurroundedUnoccupiedBlocks = (
    pattern: number[][],
    startingCol: number,
    board: Board,
    totalColumns: number,
    totalRows: number
  ) => {
    // Simulate placing the block at its lowest position
    const tempBoard = simulateBlockPlacement(pattern, startingCol, board, totalColumns, totalRows);

    // Count unoccupied blocks that are fully surrounded by occupied blocks
    let surroundedCount = 0;
    for (let row = 0; row < totalRows; row++) {
      // Ensure the row exists in tempBoard
      if (!tempBoard[row]) {
        tempBoard[row] = Array.from({ length: totalColumns }, () => ({ occupied: false }));
      }
      
      for (let col = 0; col < totalColumns; col++) {
        // Ensure the column exists in tempBoard
        if (!tempBoard[row][col]) {
          tempBoard[row][col] = { occupied: false };
        }
        
        if (!tempBoard[row][col].occupied) {
          // Check if this unoccupied block is fully surrounded by occupied blocks
          const isSurrounded = (
            (row === 0 || (tempBoard[row - 1] && tempBoard[row - 1][col] && tempBoard[row - 1][col].occupied)) && // top
            (row === totalRows - 1 || (tempBoard[row + 1] && tempBoard[row + 1][col] && tempBoard[row + 1][col].occupied)) && // bottom
            (col === 0 || (tempBoard[row][col - 1] && tempBoard[row][col - 1].occupied)) && // left
            (col === totalColumns - 1 || (tempBoard[row][col + 1] && tempBoard[row][col + 1].occupied)) // right
          );
          if (isSurrounded) {
            surroundedCount++;
          }
        }
      }
    }

    return surroundedCount;
  };

  // Function to count unoccupied blocks that have an occupied block directly above them
  const getUnoccupiedBlocksWithOccupiedAbove = (
    pattern: number[][],
    startingCol: number,
    board: Board,
    totalColumns: number,
    totalRows: number
  ) => {
    // Simulate placing the block at its lowest position
    const tempBoard = simulateBlockPlacement(pattern, startingCol, board, totalColumns, totalRows);
    
    // Debug: Log the placement details
    //console.log(`getUnoccupiedBlocksWithOccupiedAbove: pattern placed at startingCol=${startingCol}`);

    // Count unoccupied blocks that have an occupied block directly above them
    // Start from the bottom row and work upwards
    let count = 0;
    for (let row = totalRows - 1; row >= 0; row--) {
      for (let col = 0; col < totalColumns; col++) {
        // Check if current position is unoccupied and has an occupied block above it
        if (!tempBoard[row]?.[col]?.occupied && 
            row > 0 && 
            (tempBoard[row - 1]?.[col]?.occupied)) {
          count++;
        }
      }
    }

    //console.log(`getUnoccupiedBlocksWithOccupiedAbove: count=${count} unoccupied blocks with occupied above`);
    return count;
  };

  // Copy of getUnoccupiedBlocksWithOccupiedAbove named getLowestTop
  const getLowestTop = (
    pattern: number[][],
    startingCol: number,
    board: Board,
    totalColumns: number,
    totalRows: number
  ) => {
    // Simulate placing the block at its lowest position
    const tempBoard = simulateBlockPlacement(pattern, startingCol, board, totalColumns, totalRows);
    
    // Debug: Log the placement details
    //console.log(`getLowestTop: pattern placed at startingCol=${startingCol}`);

    // Find the top-most (smallest row index) where the occupied status differs between the
    // original board and the simulated board with the placed block
    for (let row = 0; row < totalRows; row++) {
      for (let col = 0; col < totalColumns; col++) {
        const originalOccupied = !!board[row]?.[col]?.occupied;
        const tempOccupied = !!tempBoard[row]?.[col]?.occupied;
        if (originalOccupied !== tempOccupied) {
          //console.log(`getLowestTop: top-most differing row=${row} at col=${col}`);
          return row;
        }
      }
    }

    // If no differences are found, return totalRows as a sentinel indicating no change
    //console.log(`getLowestTop: no differences detected; returning ${totalRows}`);
    return totalRows;
  };

  // Refactor getBestHorizontalOffsetForBoard to use the new getLowestRowByUnoccupiedBlocks function
  const getBestHorizontalOffsetForBoard = (blockType: string, board: Board, totalColumns: number, totalRows: number) => {
    let bestResult = {
      startingCol: 0,
      rotations: 0,
      unoccupiedBlocksWithOccupiedAbove: Infinity,
      lowestTopRow: -Infinity
    };
    let ties: { startingCol: number; rotations: number; unoccupiedBlocksWithOccupiedAbove: number; lowestTopRow: number }[] = [];

    for (let r = 0; r < 4; r++) {
      // Use the new rotateMatrix selector to get the correct rotation state
      let tempPattern = rotateMatrix(blockPatterns[blockType], r);
      
      const { minCol, maxCol } = getPatternColBounds(tempPattern);
      
      // Ensure the offset range keeps the pattern within valid board bounds
      const minOffset = Math.min(-minCol, 0); // Don't allow negative starting columns
      const maxOffset = Math.max(0, totalColumns - 1 - maxCol); // Don't exceed board width
      
      // Additional safety check to prevent placement too far right
      //if (maxOffset >= totalColumns) {
      // console.warn(`Invalid maxOffset: ${maxOffset} for totalColumns: ${totalColumns}, maxCol: ${maxCol}`);
      //  continue;
      //}
      
      // Debug for line pattern
      if (blockType === 'line') {
        //console.log(`Line pattern - rotation ${r}: minCol=${minCol}, maxCol=${maxCol}, minOffset=${minOffset}, maxOffset=${maxOffset}, totalColumns=${totalColumns}`);
      }
      
      for (let offset = minOffset; offset <= maxOffset; offset++) {
        // Additional safety check to ensure the pattern fits within board bounds
        if (offset + maxCol >= totalColumns) {
          //console.warn(`Skipping offset ${offset} - pattern would extend beyond board (offset + maxCol = ${offset + maxCol} >= ${totalColumns})`);
          continue;
        }
        
        //const result = getLowestRowByUnoccupiedBlocks(tempPattern, offset, board, totalColumns, totalRows);
        const unoccupiedBlocksWithOccupiedAbove = getUnoccupiedBlocksWithOccupiedAbove(tempPattern, offset, board, totalColumns, totalRows);
        //const surroundedBlocks = getSurroundedUnoccupiedBlocks(tempPattern, offset, board, totalColumns, totalRows);
        
        // Lower-priority: compute the top-most differing row between board and simulated board, and maximize it (prefer lower placement)
        const lowestTopRow = getLowestTop(tempPattern, offset, board, totalColumns, totalRows);
        
        // Optimize in order:
        // 1. fewest unoccupied blocks with occupied above (after block placement)
        // 2. maximize lowestTopRow (higher means lower placement on the board)
        const isBetter = (
          unoccupiedBlocksWithOccupiedAbove < bestResult.unoccupiedBlocksWithOccupiedAbove ||
          (unoccupiedBlocksWithOccupiedAbove === bestResult.unoccupiedBlocksWithOccupiedAbove &&
           lowestTopRow > bestResult.lowestTopRow)
        );
        
        if (isBetter) {
          bestResult = { 
            startingCol: offset, 
            rotations: r, 
            unoccupiedBlocksWithOccupiedAbove: unoccupiedBlocksWithOccupiedAbove,
            lowestTopRow: lowestTopRow
          };
          ties = [bestResult];
        } else if (
          unoccupiedBlocksWithOccupiedAbove === bestResult.unoccupiedBlocksWithOccupiedAbove &&
          lowestTopRow === bestResult.lowestTopRow
        ) {
          ties.push({ 
            startingCol: offset, 
            rotations: r, 
            unoccupiedBlocksWithOccupiedAbove: unoccupiedBlocksWithOccupiedAbove,
            lowestTopRow: lowestTopRow
          });
        }
      }
    }
    //console.log(`getBestHorizontalOffsetForBoard: ties=${JSON.stringify(ties)}`);

    const finalChoice = ties[Math.floor(Math.random() * ties.length)];
    //console.log('selected pattern', rotateMatrix(blockPatterns[blockType], finalChoice.rotations));
    
    // Debug for line pattern
    //if (blockType === 'line') {
      //console.log(`Line pattern - Final choice: startingCol=${finalChoice.startingCol}, rotations=${finalChoice.rotations}`);
    //}
    //console.log('selected pattern', rotateMatrix(blockPatterns[blockType], finalChoice.rotations));
    
    return { startingCol: finalChoice.startingCol, rotations: finalChoice.rotations };
  };

  // Function to mark a block group as stuck
  const markBlockGroupAsStuck = (id: string, pattern: number[][], stuckBlocks: StuckBlock[]) => {
    // Remove from moving block groups
    setMovingBlockGroups(prev => prev.filter(group => group.id !== id));
  };

  // Function to detect complete rows (returns row indices)
  const detectCompleteRows = (board: Board): number[] => {
    const completeRows: number[] = [];
    for (let row = 0; row < totalRows; row++) {
      let occupiedCount = 0;
      for (let col = 0; col < gridColumns; col++) {
        if (board[row]?.[col]?.occupied) {
          occupiedCount++;
        }
      }
      if (occupiedCount === gridColumns) {
        completeRows.push(row);
      }
    }
    return completeRows;
  };

  // Function to actually remove rows after animation
  const removeRowsFromBoard = (board: Board, rowsToRemove: number[]): Board => {
    const newBoard = [...board];
    // Remove rows from bottom to top to maintain correct indices
    const sortedRows = [...rowsToRemove].sort((a, b) => b - a);
    
    // First, remove all the rows (from bottom to top so indices stay correct)
    sortedRows.forEach(row => {
      newBoard.splice(row, 1);
    });
    
    // Then, add empty rows at the top (one for each removed row)
    const emptyRows: Board = Array.from({ length: rowsToRemove.length }, () =>
      Array.from({ length: gridColumns }, () => ({ occupied: false }))
    );
    
    return [...emptyRows, ...newBoard];
  };

  const handleBlockStuck = (blocks: StuckBlock[], blockGroupId: string, finalPattern: number[][], isTopRowStuck: boolean) => {
    //console.log(`Block group ${blockGroupId} got stuck with ${blocks.length} blocks`);
    
    // Find the moving block group that got stuck
    const stuckGroup = movingBlockGroups.find(group => group.id === blockGroupId);
    if (!stuckGroup) {
      return;
    }
    
    markBlockGroupAsStuck(blockGroupId, finalPattern, blocks); // Use finalPattern
    
    // Update the board with stuck blocks
    if (isTopRowStuck == false) {
      // Board is not cleared here
      setBoard(prevBoard => {
        const newBoard = [...prevBoard];
        blocks.forEach(block => {
          if (block.row >= 0 && block.row < totalRows && block.col >= 0 && block.col < gridColumns) {
            newBoard[block.row][block.col] = { occupied: true, color: block.color };
          }
        });
        
        // Detect complete rows and trigger flicker animation
        const completeRows = detectCompleteRows(newBoard);
        if (completeRows.length > 0) {
          setFlickeringRows(new Set(completeRows));
          setFlickerOpacity(1);
          // Set flag to create new block after flickering completes
          //setPendingNewBlock(true);
        } else {
          // No complete rows, schedule a new block immediately
          setPendingNewBlock(true);
        }
        
        return newBoard;
      });
    } else {
      // Board is cleared here
      const totalColumns = Math.floor(window.innerWidth / blockSize);
      const totalRows = 12;
      const emptyBoard: Board = Array.from({ length: totalRows }, () =>
        Array.from({ length: gridColumns || totalColumns }, () => ({ occupied: false }))
      );
      // Clearing the board
      setBoard(prevBoard => {return emptyBoard});
      // After clearing the board, schedule a new block
      setPendingNewBlock(true);
    }
  };

  // Calculate the center offset for positioning stuck blocks
  const getCenterOffset = () => {
    const screenWidth = window.innerWidth;
    const groupWidth = blockSize * 4;
    return (screenWidth - groupWidth) / 2;
  };

  // Calculate the starting column for block groups (same logic as BlockGroup component)
  const getStartingCol = () => {
    const totalColumns = Math.floor(window.innerWidth / blockSize);
    const currentColumn = Math.floor(totalColumns / 2);
    return currentColumn - 2; // This centers the 4-column pattern around currentColumn
  };

  // Component to render stuck blocks
  const StuckBlocksRenderer = () => {
    return (
      <div 
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.floor(window.innerWidth / blockSize)}, ${blockSize}px)`,
          gridTemplateRows: `repeat(12, ${blockSize}px)`,
          zIndex: 2,
          width: `${Math.floor(window.innerWidth / blockSize) * blockSize}px`,
          height: `${12 * blockSize}px`
        }}
      >
        {/* Render all grid cells */}
        {Array.from({ length: 12 * Math.floor(window.innerWidth / blockSize) }, (_, index) => {
          const row = Math.floor(index / Math.floor(window.innerWidth / blockSize));
          const col = index % Math.floor(window.innerWidth / blockSize);
          
          // Check if this position has a stuck block
          const stuckBlock = board[row][col];
          const isFlickering = flickeringRows.has(row);
          
          return (
            <div
              key={`stuck-grid-${index}`}
              style={{
                width: `${blockSize}px`,
                height: `${blockSize}px`,
                backgroundColor: stuckBlock?.color || 'transparent',
                border: stuckBlock?.occupied ? '5px solid #3333cc' : 'none',
                boxSizing: 'border-box',
                opacity: isFlickering ? flickerOpacity : 1,
                transition: isFlickering ? 'opacity 0.1s ease-in-out' : 'none'
              }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="App" style={{ '--block-size': `${blockSize}px` } as React.CSSProperties}>
      {/* Debug info */}
      <div style={{
        position: 'fixed',
        top: '10px',
        left: '10px',
        color: 'white',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: '10px',
        borderRadius: '5px',
        fontSize: '12px',
        fontFamily: 'monospace',
        zIndex: 10
      }}>
        Stuck Blocks: {board.flat().filter(cell => cell.occupied).length}
      </div>
      
      <div className="grid-background">
        {/* Grid will be created with CSS */}
      </div>
      <div 
        className="gray-blocks"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2
        }}
      >
        {blocks.map((index) => (
          <div key={index} className="gray-block"></div>
        ))}
      </div>
      
      {/* Render stuck blocks */}
      <StuckBlocksRenderer />
      
      {/* Render all moving block groups */}
      {movingBlockGroups.map((blockGroup) => (
        <BlockGroup 
          key={blockGroup.id}
          blockSize={blockSize} 
          pattern={blockGroup.pattern} 
          blockType={blockGroup.blockType} // Add blockType
          blockPatterns={blockPatterns} // Add blockPatterns
          startDelay={blockGroup.startDelay}
          onBlockStuck={(blocks, finalPattern, isTopRowStuck) => handleBlockStuck(blocks, blockGroup.id, finalPattern, isTopRowStuck)} // Pass finalPattern
          board={board}
          startingCol={blockGroup.optimalCol}
          color={'#4444ff'}
          startingRotation={blockGroup.startingRotation}
          optimalRotation={blockGroup.optimalRotation}
          onTopRowStuck={() => {
            const totalColumns = Math.floor(window.innerWidth / blockSize);
            const totalRows = 12;
            const emptyBoard: Board = Array.from({ length: totalRows }, () =>
              Array.from({ length: gridColumns || totalColumns }, () => ({ occupied: false }))
            );
            // Clearing the board
            setBoard(emptyBoard);
            /*setMovingBlockGroups(() => {
              const blockType = getRandomBlockType();
              const { startingCol: optimalCol, rotations: optimalRotation } = getBestHorizontalOffsetForBoard(blockType, emptyBoard, totalColumns, totalRows);
              let tempPattern = rotateMatrix(blockPatterns[blockType], 0);
              const newBlockGroup: MovingBlockGroup = {
                id: `block-group-${Date.now()}`,
                pattern: tempPattern,
                blockType: blockType,
                startDelay: 0,
                isActive: true,
                optimalCol,
                startingRotation: Math.floor(Math.random()*4),
                optimalRotation,
              };
              //console.log(`Board cleared after top-row stick. Creating new block group: ${newBlockGroup.id} at col ${optimalCol} with rotation ${optimalRotation}`);
              return [newBlockGroup];
            });*/
          }}
        />
      ))}
    </div>
  );
}

export default App;